import { useState, useMemo, useCallback, memo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckIcon, XMarkIcon, EyeIcon, ArrowUpTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { StatusBadge, RejectModal } from '../../components/workflow';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';
import { Button } from '../../components/common/Button';
import { ImportEntriesModal } from '../../components/time/ImportEntriesModal';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import {
  getAllTimesheets,
  adminApproveTimesheet,
  adminRejectTimesheet,
  adminSubmitTimesheet,
  adminDeleteTimesheet,
  bulkAdminSubmitTimesheets,
  bulkAdminApproveTimesheets,
  bulkAdminRejectTimesheets,
  bulkAdminDeleteTimesheets,
  type TimesheetSummary,
  type TimesheetStatus,
} from '../../services/api/timeEntry';
import { getPeriods } from '../../services/api/admin';
import { useProjects } from '../../hooks/useProjects';
import { useAdminUsers } from '../../hooks/useAdmin';
import { formatDisplayDate } from '../../utils/format';

function formatDate(dateString: string): string {
  return formatDisplayDate(dateString);
}

function getUserName(user: TimesheetSummary['user']): string {
  if (!user) return 'Unknown';
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.email;
}

// Memoized row component to prevent unnecessary re-renders
interface TimesheetRowProps {
  timesheet: TimesheetSummary;
  isSelected: boolean;
  onToggleSelect: (id: number) => void;
  onSubmitClick: (timesheet: TimesheetSummary) => void;
  onApproveClick: (timesheet: TimesheetSummary) => void;
  onRejectClick: (timesheet: TimesheetSummary) => void;
  onDeleteClick: (timesheet: TimesheetSummary) => void;
  canApproveReject: boolean;
}

const TimesheetRow = memo(function TimesheetRow({
  timesheet,
  isSelected,
  onToggleSelect,
  onSubmitClick,
  onApproveClick,
  onRejectClick,
  onDeleteClick,
  canApproveReject,
}: TimesheetRowProps) {
  return (
    <tr className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
      <td className="px-4 py-4 whitespace-nowrap">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(timesheet.id)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="h-10 w-10 flex-shrink-0">
            <span className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600">
              {timesheet.user?.firstName?.[0] || timesheet.user?.email[0]?.toUpperCase()}
            </span>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {getUserName(timesheet.user)}
            </div>
            <div className="text-sm text-gray-500">
              {timesheet.user?.email}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">{timesheet.period.name}</div>
        <div className="text-xs text-gray-500">
          {formatDate(timesheet.period.from)} - {formatDate(timesheet.period.to)}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">
          {timesheet.totalHours.toFixed(1)} hours
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {timesheet.submittedAt ? formatDate(timesheet.submittedAt) : '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <StatusBadge status={timesheet.state} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/admin/timesheets/${timesheet.id}`}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="View details"
          >
            <EyeIcon className="h-5 w-5" />
          </Link>
          {timesheet.state === 'open' && (
            <>
              <button
                onClick={() => onSubmitClick(timesheet)}
                className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg"
              >
                Submit
              </button>
              <button
                onClick={() => onDeleteClick(timesheet)}
                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                title="Delete"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </>
          )}
          {canApproveReject && timesheet.state === 'submitted' && (
            <>
              <button
                onClick={() => onApproveClick(timesheet)}
                className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg"
                title="Approve all"
              >
                <CheckIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => onRejectClick(timesheet)}
                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                title="Reject all"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
});

const STATE_OPTIONS: { value: TimesheetStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'submitted', label: 'Pending Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export function AdminPendingTimesheetsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTimesheet, setSelectedTimesheet] = useState<TimesheetSummary | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [showBulkSubmitModal, setShowBulkSubmitModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // F3-003: surface mutation rejections so a 4xx/5xx never leaves the
  // admin staring at a frozen modal. All eight mutations below funnel
  // through `handleMutationError`, which closes the modal that was open
  // and shows an inline banner.
  const [actionError, setActionError] = useState<string | null>(null);
  const handleMutationError = useCallback((err: unknown) => {
    const msg = err instanceof Error ? err.message : 'Action failed';
    setActionError(msg);
    setShowApproveModal(false);
    setShowRejectModal(false);
    setShowSubmitModal(false);
    setShowDeleteModal(false);
    setShowBulkApproveModal(false);
    setShowBulkRejectModal(false);
    setShowBulkSubmitModal(false);
    setShowBulkDeleteModal(false);
  }, []);

  // Read filters from URL. The Status chips are additive: an empty selection means
  // "no state filter" — every state is listed. Picking a chip narrows to it, picking
  // it again widens back. Unknown tokens (notably the legacy `?state=all` sentinel
  // that TimesheetsSection and BalicekHeroCard still link with) are dropped here, so
  // they land as an empty selection instead of leaking into the set and producing
  // nonsense like `all,open` on the next chip click.
  const stateFilterRaw = searchParams.get('state') ?? '';
  const stateFilterSet = useMemo(() => {
    const valid = new Set<string>(STATE_OPTIONS.map((o) => o.value));
    return new Set(stateFilterRaw.split(',').filter((s) => valid.has(s)));
  }, [stateFilterRaw]);
  const stateFilterParam =
    stateFilterSet.size === 0 || stateFilterSet.size === STATE_OPTIONS.length
      ? undefined
      : Array.from(stateFilterSet).join(',');
  const periodFilter = searchParams.get('periodId') || '';
  const projectFilter = searchParams.get('projectId') || '';
  const userFilter = searchParams.get('userId') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = 50;

  const toggleStateFilter = (state: TimesheetStatus) => {
    const next = new Set(stateFilterSet);
    if (next.has(state)) {
      next.delete(state); // Deselecting the last chip is allowed — empty means "all states"
    } else {
      next.add(state);
    }
    setFilter('state', Array.from(next).join(','));
  };

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    // Reset to page 1 when filters change
    params.delete('page');
    setSearchParams(params, { replace: true });
    // Clear selection when filters change
    setSelectedIds(new Set());
  };

  const setPage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page > 1) {
      params.set('page', String(page));
    } else {
      params.delete('page');
    }
    setSearchParams(params, { replace: true });
    setSelectedIds(new Set());
  };

  const setGranularity = (g: 'month' | 'quarter' | 'year') => {
    const params = new URLSearchParams(searchParams);
    params.set('gran', g);
    params.delete('page');
    if (g === 'month') {
      params.delete('year');
      params.delete('quarter');
    } else {
      // Period (single month) makes no sense across a range.
      params.delete('periodId');
      if (!params.get('year')) params.set('year', String(new Date().getFullYear()));
      if (g === 'quarter' && !params.get('quarter')) params.set('quarter', '1');
    }
    setSearchParams(params, { replace: true });
    setSelectedIds(new Set());
  };

  const toggleSort = (col: 'employee' | 'period' | 'status') => {
    const params = new URLSearchParams(searchParams);
    const nextDir = searchParams.get('sort') === col && searchParams.get('dir') === 'asc' ? 'desc' : 'asc';
    params.set('sort', col);
    params.set('dir', nextDir);
    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const { data: periods = [] } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: projects = [] } = useProjects({ state: 'in_progress' });
  const { data: users = [] } = useAdminUsers();

  // ── Granularity (month / quarter / year) ──────────────────────────────
  // 'month' uses the single-period dropdown (periodId). 'quarter'/'year'
  // resolve to a [fromDate, toDate] range covering several monthly periods.
  const granularity = (searchParams.get('gran') as 'month' | 'quarter' | 'year') || 'month';
  const yearFilter = searchParams.get('year') || String(new Date().getFullYear());
  const quarterFilter = searchParams.get('quarter') || '1';

  // ── Sort (DB-backed columns only) ─────────────────────────────────────
  const sortBy = (searchParams.get('sort') as 'employee' | 'period' | 'status' | null) || undefined;
  const sortOrder = (searchParams.get('dir') as 'asc' | 'desc' | null) || undefined;

  const availableYears = useMemo(() => {
    const ys = new Set<number>();
    for (const p of periods) {
      if (p.from) ys.add(new Date(p.from).getUTCFullYear());
    }
    ys.add(new Date().getFullYear());
    return Array.from(ys).sort((a, b) => b - a);
  }, [periods]);

  const { rangeFrom, rangeTo } = useMemo(() => {
    if (granularity === 'year') {
      return { rangeFrom: `${yearFilter}-01-01`, rangeTo: `${yearFilter}-12-31` };
    }
    if (granularity === 'quarter') {
      const q = parseInt(quarterFilter, 10);
      const startMonth = (q - 1) * 3 + 1; // 1, 4, 7, 10
      const endMonth = startMonth + 2; // 3, 6, 9, 12
      const lastDay = new Date(parseInt(yearFilter, 10), endMonth, 0).getDate();
      return {
        rangeFrom: `${yearFilter}-${String(startMonth).padStart(2, '0')}-01`,
        rangeTo: `${yearFilter}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
      };
    }
    return { rangeFrom: undefined as string | undefined, rangeTo: undefined as string | undefined };
  }, [granularity, yearFilter, quarterFilter]);

  // periodId only applies in month granularity; quarter/year use the range.
  const effectivePeriodId =
    granularity === 'month' && periodFilter ? parseInt(periodFilter, 10) : undefined;

  // Query for paginated data (no aggregate)
  const {
    data: timesheetsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['adminTimesheets', stateFilterParam, granularity, periodFilter, rangeFrom, rangeTo, projectFilter, userFilter, sortBy, sortOrder, currentPage],
    queryFn: () =>
      getAllTimesheets(
        currentPage,
        stateFilterParam,
        effectivePeriodId,
        userFilter ? parseInt(userFilter, 10) : undefined,
        projectFilter ? parseInt(projectFilter, 10) : undefined,
        pageSize,
        false, // Don't include aggregate - fetched separately
        { fromDate: rangeFrom, toDate: rangeTo, sortBy, sortOrder }
      ),
  });

  // Separate query for aggregates (doesn't change with page, only with filters)
  const { data: aggregateData } = useQuery({
    queryKey: ['adminTimesheetsAggregate', stateFilterParam, granularity, periodFilter, rangeFrom, rangeTo, projectFilter, userFilter],
    queryFn: () =>
      getAllTimesheets(
        1, // Always page 1 for aggregate query
        stateFilterParam,
        effectivePeriodId,
        userFilter ? parseInt(userFilter, 10) : undefined,
        projectFilter ? parseInt(projectFilter, 10) : undefined,
        1, // Minimal page size since we only need aggregate
        true, // Include aggregate
        { fromDate: rangeFrom, toDate: rangeTo } // sort irrelevant for aggregate
      ),
    staleTime: 60 * 1000, // Cache for 1 minute
  });

  const pagination = timesheetsData?.pagination;

  const timesheets = useMemo(() => timesheetsData?.data || [], [timesheetsData?.data]);

  // Use aggregate stats from separate query (doesn't change with page)
  const aggregate = aggregateData?.aggregate;
  const hoursByState = useMemo(() => {
    if (!aggregate?.hoursByState) {
      return {
        total: 0,
        approved: 0,
        submitted: 0,
        open: 0,
        rejected: 0,
        count: { total: 0, approved: 0, submitted: 0, open: 0, rejected: 0 },
      };
    }
    // When filtering by project, hoursByState contains entry-level state breakdown
    // Otherwise, it contains timesheet-level state breakdown
    const countByState = aggregate.countByState ?? { approved: 0, submitted: 0, open: 0, rejected: 0 };
    return {
      total: aggregate.totalHours,
      approved: aggregate.hoursByState.approved,
      submitted: aggregate.hoursByState.submitted,
      open: aggregate.hoursByState.open,
      rejected: aggregate.hoursByState.rejected,
      count: {
        total: countByState.approved + countByState.submitted + countByState.open + countByState.rejected,
        approved: countByState.approved,
        submitted: countByState.submitted,
        open: countByState.open,
        rejected: countByState.rejected,
      },
    };
  }, [aggregate]);

  // Compute selected timesheets info
  const selectedTimesheets = useMemo(() => {
    return timesheets.filter((t) => selectedIds.has(t.id));
  }, [timesheets, selectedIds]);

  const selectedState = useMemo(() => {
    if (selectedTimesheets.length === 0) return null;
    const states = new Set(selectedTimesheets.map((t) => t.state));
    return states.size === 1 ? selectedTimesheets[0]?.state ?? null : null;
  }, [selectedTimesheets]);

  const approveMutation = useMutation({
    mutationFn: (id: number) => adminApproveTimesheet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminTimesheets'] });
      queryClient.invalidateQueries({ queryKey: ['pendingTimesheets'] });
      setShowApproveModal(false);
      setSelectedTimesheet(null);
      setActionError(null);
    },
    onError: handleMutationError,
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => adminRejectTimesheet(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminTimesheets'] });
      queryClient.invalidateQueries({ queryKey: ['pendingTimesheets'] });
      setShowRejectModal(false);
      setSelectedTimesheet(null);
      setActionError(null);
    },
    onError: handleMutationError,
  });

  const submitMutation = useMutation({
    mutationFn: (id: number) => adminSubmitTimesheet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminTimesheets'] });
      queryClient.invalidateQueries({ queryKey: ['pendingTimesheets'] });
      setShowSubmitModal(false);
      setSelectedTimesheet(null);
      setActionError(null);
    },
    onError: handleMutationError,
  });

  // Bulk mutations
  const bulkApproveMutation = useMutation({
    mutationFn: (ids: number[]) => bulkAdminApproveTimesheets(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminTimesheets'] });
      queryClient.invalidateQueries({ queryKey: ['pendingTimesheets'] });
      setShowBulkApproveModal(false);
      setSelectedIds(new Set());
      setActionError(null);
    },
    onError: handleMutationError,
  });

  const bulkRejectMutation = useMutation({
    mutationFn: ({ ids, reason }: { ids: number[]; reason: string }) =>
      bulkAdminRejectTimesheets(ids, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminTimesheets'] });
      queryClient.invalidateQueries({ queryKey: ['pendingTimesheets'] });
      setShowBulkRejectModal(false);
      setSelectedIds(new Set());
      setActionError(null);
    },
    onError: handleMutationError,
  });

  const bulkSubmitMutation = useMutation({
    mutationFn: (ids: number[]) => bulkAdminSubmitTimesheets(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminTimesheets'] });
      queryClient.invalidateQueries({ queryKey: ['pendingTimesheets'] });
      setShowBulkSubmitModal(false);
      setSelectedIds(new Set());
      setActionError(null);
    },
    onError: handleMutationError,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminDeleteTimesheet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminTimesheets'] });
      queryClient.invalidateQueries({ queryKey: ['pendingTimesheets'] });
      setShowDeleteModal(false);
      setSelectedTimesheet(null);
      setActionError(null);
    },
    onError: handleMutationError,
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: number[]) => bulkAdminDeleteTimesheets(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminTimesheets'] });
      queryClient.invalidateQueries({ queryKey: ['pendingTimesheets'] });
      setShowBulkDeleteModal(false);
      setSelectedIds(new Set());
      setActionError(null);
    },
    onError: handleMutationError,
  });

  const handleApprove = () => {
    if (selectedTimesheet) {
      approveMutation.mutate(selectedTimesheet.id);
    }
  };

  const handleReject = (reason: string) => {
    if (selectedTimesheet) {
      rejectMutation.mutate({ id: selectedTimesheet.id, reason });
    }
  };

  const handleSubmit = () => {
    if (selectedTimesheet) {
      submitMutation.mutate(selectedTimesheet.id);
    }
  };

  // Bulk handlers
  const handleBulkApprove = () => {
    bulkApproveMutation.mutate(Array.from(selectedIds));
  };

  const handleBulkReject = (reason: string) => {
    bulkRejectMutation.mutate({ ids: Array.from(selectedIds), reason });
  };

  const handleBulkSubmit = () => {
    bulkSubmitMutation.mutate(Array.from(selectedIds));
  };

  // Selection handlers - memoized to prevent unnecessary re-renders
  const toggleSelection = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === timesheets.length) {
        return new Set();
      }
      return new Set(timesheets.map((t) => t.id));
    });
  }, [timesheets]);

  // Memoized click handlers
  const handleApproveClick = useCallback((timesheet: TimesheetSummary) => {
    setSelectedTimesheet(timesheet);
    setShowApproveModal(true);
  }, []);

  const handleRejectClick = useCallback((timesheet: TimesheetSummary) => {
    setSelectedTimesheet(timesheet);
    setShowRejectModal(true);
  }, []);

  const handleSubmitClick = useCallback((timesheet: TimesheetSummary) => {
    setSelectedTimesheet(timesheet);
    setShowSubmitModal(true);
  }, []);

  const handleDeleteClick = useCallback((timesheet: TimesheetSummary) => {
    setSelectedTimesheet(timesheet);
    setShowDeleteModal(true);
  }, []);

  const handleDelete = () => {
    if (selectedTimesheet) {
      deleteMutation.mutate(selectedTimesheet.id);
    }
  };

  const handleBulkDelete = () => {
    bulkDeleteMutation.mutate(Array.from(selectedIds));
  };

  const canApproveReject = stateFilterSet.has('submitted') || stateFilterSet.size === 0;
  const isAllSelected = timesheets.length > 0 && selectedIds.size === timesheets.length;
  const isSomeSelected = selectedIds.size > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Timesheet Approval</h1>
          <p className="mt-1 text-sm font-medium text-amber-600 bg-amber-50 inline-block px-2 py-1 rounded">
            Full timesheet approval (all entries)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={() => setShowImportModal(true)}>
            <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
            Import
          </Button>
          <Link
            to="/manager/timesheets/pending"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            View my approvals
          </Link>
        </div>
      </div>

      <DismissibleErrorBanner
        message={actionError}
        onDismiss={() => setActionError(null)}
        role="alert"
      />

      {/* Filters */}
      <div className="flex items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <div className="flex items-center gap-1.5">
            {STATE_OPTIONS.map((opt) => {
              const isActive = stateFilterSet.has(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => toggleStateFilter(opt.value)}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                    isActive
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Granularity</label>
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as 'month' | 'quarter' | 'year')}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            <option value="month">Month</option>
            <option value="quarter">Quarter</option>
            <option value="year">Year</option>
          </select>
        </div>
        {granularity === 'month' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
            <select
              value={periodFilter}
              onChange={(e) => setFilter('periodId', e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">- All -</option>
              {periods.map((period) => (
                <option key={period.id} value={period.id}>{period.name}</option>
              ))}
            </select>
          </div>
        )}
        {granularity === 'quarter' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
            <select
              value={quarterFilter}
              onChange={(e) => setFilter('quarter', e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="1">Q1 (Jan–Mar)</option>
              <option value="2">Q2 (Apr–Jun)</option>
              <option value="3">Q3 (Jul–Sep)</option>
              <option value="4">Q4 (Oct–Dec)</option>
            </select>
          </div>
        )}
        {granularity !== 'month' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={yearFilter}
              onChange={(e) => setFilter('year', e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              {availableYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
          <select
            value={projectFilter}
            onChange={(e) => setFilter('projectId', e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            <option value="">- All -</option>
            {[...projects]
              .sort((a, b) => (a.fullName || a.name).localeCompare(b.fullName || b.name))
              .map((project) => (
                <option key={project.id} value={project.id}>{project.fullName || project.name}</option>
              ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
          <select
            value={userFilter}
            onChange={(e) => setFilter('userId', e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            <option value="">- All -</option>
            {[...users]
              .sort((a, b) => {
                const nameA = a.lastName || a.firstName || a.email;
                const nameB = b.lastName || b.firstName || b.email;
                return nameA.localeCompare(nameB);
              })
              .map((user) => (
                <option key={user.id} value={user.id}>
                  {user.lastName && user.firstName
                    ? `${user.lastName}, ${user.firstName}`
                    : user.email}
                </option>
              ))}
          </select>
        </div>
        {(periodFilter || projectFilter || userFilter || granularity !== 'month' || sortBy || stateFilterSet.size > 0) && (
          <button
            onClick={() => setSearchParams({}, { replace: true })}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium self-end pb-2"
          >
            Reset filters
          </button>
        )}
      </div>

      {/* Total Hours Summary */}
      {!isLoading && timesheets.length > 0 && (
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <span className="text-sm font-medium text-gray-600">Total Hours</span>
              <div className="text-3xl font-bold text-gray-800">{hoursByState.total.toFixed(1)}</div>
              <span className="text-xs text-gray-500">{hoursByState.count.total} timesheets</span>
            </div>
            <div className="flex items-center gap-6">
              {hoursByState.count.approved > 0 && (
                <div className="text-center">
                  <span className="text-xs font-medium text-green-600 uppercase">Approved</span>
                  <div className="text-xl font-bold text-green-600">{hoursByState.approved.toFixed(1)}</div>
                  <span className="text-xs text-gray-500">{hoursByState.count.approved} ts</span>
                </div>
              )}
              {hoursByState.count.submitted > 0 && (
                <div className="text-center">
                  <span className="text-xs font-medium text-blue-600 uppercase">Submitted</span>
                  <div className="text-xl font-bold text-blue-600">{hoursByState.submitted.toFixed(1)}</div>
                  <span className="text-xs text-gray-500">{hoursByState.count.submitted} ts</span>
                </div>
              )}
              {hoursByState.count.open > 0 && (
                <div className="text-center">
                  <span className="text-xs font-medium text-gray-700 uppercase">Open</span>
                  <div className="text-xl font-bold text-gray-700">{hoursByState.open.toFixed(1)}</div>
                  <span className="text-xs text-gray-500">{hoursByState.count.open} ts</span>
                </div>
              )}
              {hoursByState.count.rejected > 0 && (
                <div className="text-center">
                  <span className="text-xs font-medium text-red-600 uppercase">Rejected</span>
                  <div className="text-xl font-bold text-red-600">{hoursByState.rejected.toFixed(1)}</div>
                  <span className="text-xs text-gray-500">{hoursByState.count.rejected} ts</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Bar */}
      {isSomeSelected && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-800">
            {selectedIds.size} timesheet{selectedIds.size !== 1 ? 's' : ''} selected
            {selectedState && (
              <span className="text-blue-600 ml-1">
                (all {selectedState})
              </span>
            )}
            {!selectedState && selectedIds.size > 1 && (
              <span className="text-amber-600 ml-1">
                (mixed states - select same state for bulk actions)
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            {selectedState === 'open' && (
              <>
                <button
                  onClick={() => setShowBulkSubmitModal(true)}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg"
                >
                  Submit All
                </button>
                <button
                  onClick={() => setShowBulkDeleteModal(true)}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
                >
                  Delete All
                </button>
              </>
            )}
            {selectedState === 'submitted' && (
              <>
                <button
                  onClick={() => setShowBulkApproveModal(true)}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg"
                >
                  Approve All
                </button>
                <button
                  onClick={() => setShowBulkRejectModal(true)}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
                >
                  Reject All
                </button>
              </>
            )}
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="p-4 bg-red-50 text-red-700 rounded-lg m-4">
            Failed to load timesheets. Please try again.
          </div>
        ) : timesheets.length === 0 ? (
          <div className="text-center py-12">
            <CheckIcon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No timesheets found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No timesheets match the selected filters.
            </p>
          </div>
        ) : (
          <>
            {/* Top Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3 sm:px-6">
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * pageSize, pagination.total)}</span> of{' '}
                  <span className="font-medium">{pagination.total}</span> results
                </p>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => setPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {Array.from({ length: Math.min(pagination.totalPages, 7) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 7) {
                      pageNum = i + 1;
                    } else if (currentPage <= 4) {
                      pageNum = i + 1;
                    } else if (currentPage >= pagination.totalPages - 3) {
                      pageNum = pagination.totalPages - 6 + i;
                    } else {
                      pageNum = currentPage - 3 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                          pageNum === currentPage
                            ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage(currentPage + 1)}
                    disabled={currentPage >= pagination.totalPages}
                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            )}
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      type="button"
                      onClick={() => toggleSort('employee')}
                      className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-700"
                    >
                      Employee
                      <span className="text-gray-400">{sortBy === 'employee' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}</span>
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      type="button"
                      onClick={() => toggleSort('period')}
                      className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-700"
                    >
                      Period
                      <span className="text-gray-400">{sortBy === 'period' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}</span>
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      type="button"
                      onClick={() => toggleSort('status')}
                      className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-700"
                    >
                      Status
                      <span className="text-gray-400">{sortBy === 'status' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}</span>
                    </button>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {timesheets.map((timesheet) => (
                  <TimesheetRow
                    key={timesheet.id}
                    timesheet={timesheet}
                    isSelected={selectedIds.has(timesheet.id)}
                    onToggleSelect={toggleSelection}
                    onSubmitClick={handleSubmitClick}
                    onApproveClick={handleApproveClick}
                    onRejectClick={handleRejectClick}
                    onDeleteClick={handleDeleteClick}
                    canApproveReject={canApproveReject}
                  />
                ))}
              </tbody>
            </table>
          </div>

        {/* Pagination Controls */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= pagination.totalPages}
                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * pageSize, pagination.total)}</span> of{' '}
                  <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => setPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {Array.from({ length: Math.min(pagination.totalPages, 7) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 7) {
                      pageNum = i + 1;
                    } else if (currentPage <= 4) {
                      pageNum = i + 1;
                    } else if (currentPage >= pagination.totalPages - 3) {
                      pageNum = pagination.totalPages - 6 + i;
                    } else {
                      pageNum = currentPage - 3 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                          pageNum === currentPage
                            ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage(currentPage + 1)}
                    disabled={currentPage >= pagination.totalPages}
                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Single Approve Confirmation Modal */}
      <ConfirmModal
        isOpen={showApproveModal}
        onClose={() => {
          setShowApproveModal(false);
          setSelectedTimesheet(null);
        }}
        onConfirm={handleApprove}
        title="Approve Entire Timesheet"
        message={`Are you sure you want to approve ALL entries in ${getUserName(selectedTimesheet?.user ?? null)}'s timesheet for ${selectedTimesheet?.period.name}?`}
        confirmText="Approve All"
        variant="primary"
        isLoading={approveMutation.isPending}
      />

      {/* Single Reject Modal */}
      <RejectModal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setSelectedTimesheet(null);
        }}
        onConfirm={handleReject}
        isLoading={rejectMutation.isPending}
        employeeName={getUserName(selectedTimesheet?.user ?? null)}
      />

      {/* Single Submit Confirmation Modal */}
      <ConfirmModal
        isOpen={showSubmitModal}
        onClose={() => {
          setShowSubmitModal(false);
          setSelectedTimesheet(null);
        }}
        onConfirm={handleSubmit}
        title="Submit Timesheet"
        message={`Are you sure you want to submit ${getUserName(selectedTimesheet?.user ?? null)}'s timesheet for ${selectedTimesheet?.period.name} on their behalf?`}
        confirmText="Submit"
        variant="primary"
        isLoading={submitMutation.isPending}
      />

      {/* Bulk Approve Modal */}
      <ConfirmModal
        isOpen={showBulkApproveModal}
        onClose={() => setShowBulkApproveModal(false)}
        onConfirm={handleBulkApprove}
        title="Approve Multiple Timesheets"
        message={`Are you sure you want to approve ${selectedIds.size} timesheet${selectedIds.size !== 1 ? 's' : ''}?`}
        confirmText="Approve All"
        variant="primary"
        isLoading={bulkApproveMutation.isPending}
      />

      {/* Bulk Reject Modal */}
      <RejectModal
        isOpen={showBulkRejectModal}
        onClose={() => setShowBulkRejectModal(false)}
        onConfirm={handleBulkReject}
        isLoading={bulkRejectMutation.isPending}
        employeeName={`${selectedIds.size} employee${selectedIds.size !== 1 ? 's' : ''}`}
      />

      {/* Bulk Submit Modal */}
      <ConfirmModal
        isOpen={showBulkSubmitModal}
        onClose={() => setShowBulkSubmitModal(false)}
        onConfirm={handleBulkSubmit}
        title="Submit Multiple Timesheets"
        message={`Are you sure you want to submit ${selectedIds.size} timesheet${selectedIds.size !== 1 ? 's' : ''} on behalf of their owners?`}
        confirmText="Submit All"
        variant="primary"
        isLoading={bulkSubmitMutation.isPending}
      />

      {/* Single Delete Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedTimesheet(null);
        }}
        onConfirm={handleDelete}
        title="Delete Timesheet"
        message={`Are you sure you want to delete ${getUserName(selectedTimesheet?.user ?? null)}'s timesheet for ${selectedTimesheet?.period.name}? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Bulk Delete Modal */}
      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        onConfirm={handleBulkDelete}
        title="Delete Multiple Timesheets"
        message={`Are you sure you want to delete ${selectedIds.size} timesheet${selectedIds.size !== 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText="Delete All"
        variant="danger"
        isLoading={bulkDeleteMutation.isPending}
      />

      <ImportEntriesModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} />
    </div>
  );
}
